import React, { useState, useEffect, useRef } from 'react';
import VolNav from '../../components/vol/VolNav';
import { useVolunteerAuth } from '../../hooks/useVolunteerAuth';
import {
  getLocations, getShiftTimes, getVolunteers,
  subscribeToAssignments, subscribeToPortalSettings,
  selfSignUpForShift, selfCancelShift,
} from '../../services/firebase';
import './Vol.css';

const DAYS = [
  { key: 'Thu-April-9',  label: 'Thu, Apr 9',  full: 'Thursday, April 9' },
  { key: 'Fri-April-10', label: 'Fri, Apr 10', full: 'Friday, April 10' },
  { key: 'Sat-April-11', label: 'Sat, Apr 11', full: 'Saturday, April 11' },
  { key: 'Sun-April-12', label: 'Sun, Apr 12', full: 'Sunday, April 12' },
];

const DAY_AVAIL_KEY = {
  'Thu-April-9':  'thuAvailability',
  'Fri-April-10': 'friAvailability',
  'Sat-April-11': 'satAvailability',
  'Sun-April-12': 'sunAvailability',
};

const ERROR_MESSAGES = {
  SHIFT_FULL: 'This shift is now full.',
  SCHEDULING_CLOSED: 'Sign-ups for this shift are closed.',
  ALREADY_SIGNED_UP: 'You are already signed up for this shift.',
  SHIFT_NOT_FOUND: 'This shift has not been set up yet.',
};

function VolDashboard() {
  const { volunteer, firebaseUser } = useVolunteerAuth();
  const isAdmin = localStorage.getItem('adminAuth') === 'true';

  const [selectedDay, setSelectedDay] = useState(DAYS[0].key);
  const [locations, setLocations] = useState([]);
  const [shiftTimes, setShiftTimes] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [portalSettings, setPortalSettings] = useState({ selfSchedulingEnabled: false });
  const [volunteerMap, setVolunteerMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionState, setActionState] = useState({});
  const [profileOpen, setProfileOpen] = useState(false);
  const unsubAssign = useRef(null);
  const unsubPortal = useRef(null);

  useEffect(() => {
    const init = async () => {
      const [locs, shifts, vols] = await Promise.all([getLocations(), getShiftTimes(), getVolunteers()]);
      setLocations(locs.filter(l => l.active).map(l => l.name).sort());
      setShiftTimes(shifts.filter(s => s.active).sort((a, b) => a.order - b.order));
      const vm = {};
      vols.forEach(v => { vm[v.id] = v; });
      setVolunteerMap(vm);
      setLoading(false);
    };
    init();

    // Live subscriptions — updates propagate instantly without refresh
    unsubAssign.current = subscribeToAssignments(data => setAssignments(data));
    unsubPortal.current = subscribeToPortalSettings(data => setPortalSettings(data));

    return () => {
      if (unsubAssign.current) unsubAssign.current();
      if (unsubPortal.current) unsubPortal.current();
    };
  }, []);

  if (loading) {
    return (
      <div className="vol-page">
        <VolNav />
        <div className="vol-content" style={{ color: '#64748b', paddingTop: '40px' }}>Loading…</div>
      </div>
    );
  }

  const vid = volunteer?.id || null;

  // Assignment lookup
  const assignMap = {};
  assignments.forEach(a => { assignMap[`${a.day}_${a.location}_${a.shiftTime}`] = a; });
  const getA = (day, loc, shiftName) => assignMap[`${day}_${loc}_${shiftName}`] || {};

  const getLocalSlots = (a) => {
    const cap = a.localCap || 2;
    const vols = a.volunteers || [];
    const slots = [];
    for (let i = 3; i < 3 + cap; i++) slots.push(vols[i] || null);
    return slots;
  };

  const isSignedUp = (a) => vid && (a.volunteers || []).includes(vid);

  const ELEVATED_ROLES = ['cart-admin', 'driver-admin', 'smpw-admin', 'superuser'];
  const viewerRole = volunteer?.role || '';
  const canSeeFullNames = isAdmin || ELEVATED_ROLES.includes(viewerRole);

  const getCompanions = (a) => {
    const cap = a.localCap || 2;
    const vols = a.volunteers || [];
    const companions = [];
    for (let i = 3; i < 3 + cap; i++) {
      const id = vols[i];
      if (!id || id === vid) continue;
      const v = volunteerMap[id];
      if (!v) continue;
      if (canSeeFullNames) {
        companions.push(`${v.firstName} ${v.lastName}${v.congregation ? ` (${v.congregation})` : ''}`);
      } else {
        companions.push(`${v.firstName?.[0] || ''}. ${v.lastName}${v.congregation ? ` (${v.congregation})` : ''}`);
      }
    }
    return companions;
  };

  // My shifts across all days
  const myShifts = [];
  if (vid) {
    DAYS.forEach(({ key: day, label }) => {
      const dayAll = shiftTimes.filter(s => !s.days || s.days.includes(day));
      locations.forEach(loc => {
        dayAll.forEach(st => {
          const a = getA(day, loc, st.name);
          if (isSignedUp(a)) myShifts.push({ day, label, loc, shift: st.name });
        });
      });
    });
  }

  const handleAction = async (fn, loc, shiftName) => {
    const key = `${selectedDay}_${loc}_${shiftName}`;
    setActionState(s => ({ ...s, [key]: 'loading' }));
    try {
      await fn(vid, selectedDay, loc, shiftName);
      setActionState(s => ({ ...s, [key]: null }));
    } catch (err) {
      const msg = ERROR_MESSAGES[err.message] || 'Something went wrong. Please try again.';
      setActionState(s => ({ ...s, [key]: msg }));
      setTimeout(() => setActionState(s => ({ ...s, [key]: null })), 5000);
    }
  };

  const dayShifts = shiftTimes.filter(s => !s.days || s.days.includes(selectedDay));
  const openLocations = locations.filter(loc =>
    dayShifts.some(st => getA(selectedDay, loc, st.name).schedulingOpen)
  );

  const schedulingOn = portalSettings.selfSchedulingEnabled;
  const firstName = volunteer?.firstName || firebaseUser?.displayName?.split(' ')[0] || (isAdmin ? 'Admin' : 'Volunteer');

  return (
    <div className="vol-page">
      <VolNav />
      <div className="vol-content">

        {/* Header */}
        <header className="vol-page-header">
          <h1>Welcome, {firstName}!</h1>
          <p className="vol-page-subtitle">April 9–12, 2026 · Augusta, Georgia</p>
        </header>

        {/* Admin info banner */}
        {isAdmin && !volunteer && (
          <div className="vol-alert vol-alert-warn" style={{ marginBottom: '16px' }}>
            Admin view — sign in via Google on the{' '}
            <a href="/vol/login" style={{ color: '#92400e' }}>volunteer login page</a>{' '}
            with a registered email to see your own profile and sign up for shifts.
          </div>
        )}

        {/* My shifts */}
        {myShifts.length > 0 && (
          <div className="vol-card vol-my-shifts-card">
            <h2 className="vol-card-title">My Shifts ({myShifts.length})</h2>
            <div className="vol-my-shifts-list">
              {myShifts.map((s, i) => (
                <div key={i} className="vol-my-shift-row">
                  <span className="vol-my-shift-day">{s.label}</span>
                  <span className="vol-my-shift-loc">{s.loc}</span>
                  <span className="vol-my-shift-time">{s.shift}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Scheduling closed notice */}
        {!schedulingOn && (
          <div className="vol-alert vol-alert-warn">
            Shift sign-ups are not yet open. Check back soon.
          </div>
        )}

        {/* Day tabs */}
        <div className="vol-day-tabs">
          {DAYS.map(d => (
            <button
              key={d.key}
              className={`vol-day-tab${selectedDay === d.key ? ' active' : ''}`}
              onClick={() => setSelectedDay(d.key)}
            >
              {d.label}
            </button>
          ))}
        </div>

        {/* Shift cards */}
        {openLocations.length === 0 ? (
          schedulingOn && (
            <p style={{ color: '#94a3b8', fontSize: '14px', marginTop: '8px' }}>
              No shifts are open for sign-up on this day yet.
            </p>
          )
        ) : (
          openLocations.map(loc => {
            const openShifts = dayShifts.filter(st => getA(selectedDay, loc, st.name).schedulingOpen);
            return (
              <div key={loc} className="vol-location-section">
                <h3 className="vol-loc-header">{loc}</h3>
                <div className="vol-shift-cards">
                  {openShifts.map(st => {
                    const a = getA(selectedDay, loc, st.name);
                    const slots = getLocalSlots(a);
                    const cap = a.localCap || 2;
                    const filled = slots.filter(Boolean).length;
                    const full = filled >= cap;
                    const signedUp = isSignedUp(a);
                    const key = `${selectedDay}_${loc}_${st.name}`;
                    const state = actionState[key];
                    const busy = state === 'loading';
                    const errMsg = state && state !== 'loading' ? state : null;

                    return (
                      <div
                        key={st.id}
                        className={`vol-shift-card${signedUp ? ' vol-shift-signed-up' : ''}${full && !signedUp ? ' vol-shift-full' : ''}`}
                      >
                        <div className="vol-shift-time">{st.name}</div>
                        <div className="vol-shift-spots">
                          <span className={`vol-cap-badge${full ? ' full' : filled > 0 ? ' partial' : ''}`}>
                            {filled}/{cap} spots filled
                          </span>
                        </div>
                        {(() => {
                          const companions = getCompanions(a);
                          if (companions.length === 0) return null;
                          return (
                            <div className="vol-shift-companions">
                              {companions.map((c, i) => (
                                <span key={i} className="vol-companion-chip">{c}</span>
                              ))}
                            </div>
                          );
                        })()}
                        <div className="vol-shift-action">
                          {isAdmin && !vid ? (
                            <span className="vol-shift-full-tag" style={{ color: '#64748b', background: '#f1f5f9' }}>
                              Admin view
                            </span>
                          ) : signedUp ? (
                            <button
                              className="vol-btn-cancel"
                              onClick={() => handleAction(selfCancelShift, loc, st.name)}
                              disabled={busy}
                            >
                              {busy ? '…' : '✓ Joined · Cancel'}
                            </button>
                          ) : full ? (
                            <span className="vol-shift-full-tag">Full</span>
                          ) : (
                            <button
                              className="vol-btn-signup"
                              onClick={() => handleAction(selfSignUpForShift, loc, st.name)}
                              disabled={busy || !schedulingOn}
                            >
                              {busy ? 'Joining…' : 'Join Shift'}
                            </button>
                          )}
                        </div>
                        {errMsg && <div className="vol-shift-error">{errMsg}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}

        {/* Profile — collapsible section at bottom */}
        {volunteer && (
          <div className="vol-profile-section">
            <button
              className="vol-profile-toggle"
              onClick={() => setProfileOpen(o => !o)}
            >
              {profileOpen ? '▾' : '▸'} My Registration Info
            </button>
            {profileOpen && (
              <div className="vol-card" style={{ marginTop: '10px' }}>
                <div className="vol-info-row">
                  <span className="vol-info-label">Name</span>
                  <span>{volunteer.firstName} {volunteer.lastName}</span>
                </div>
                <div className="vol-info-row">
                  <span className="vol-info-label">Congregation</span>
                  <span>{volunteer.congregation || '—'}</span>
                </div>
                <div className="vol-info-row">
                  <span className="vol-info-label">Email</span>
                  <span>{volunteer.email || '—'}</span>
                </div>
                {volunteer.phone && (
                  <div className="vol-info-row">
                    <span className="vol-info-label">Phone</span>
                    <span>{volunteer.phone}</span>
                  </div>
                )}
                <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #f1f5f9' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Availability
                  </div>
                  <div className="vol-avail-list">
                    {DAYS.map(({ key, full: dayLabel }) => {
                      const avail = volunteer[DAY_AVAIL_KEY[key]];
                      const unavail = !avail || avail === '' || avail === 'Not Available';
                      return (
                        <div key={key} className={`vol-avail-row ${unavail ? 'unavail' : 'avail'}`}>
                          <span className="vol-avail-day">{dayLabel}</span>
                          <span className="vol-avail-value">
                            {unavail ? 'Not available'
                              : avail === 'Anytime' ? 'Anytime'
                              : Array.isArray(avail) ? avail.join(', ')
                              : avail}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="vol-info-banner" style={{ marginTop: '24px' }}>
          <strong>Meeting location:</strong> 4063 Jefferson Davis Hwy, Warrenville, SC 29851.{' '}
          <em>Please arrive at least 1 hour before your shift time.</em>
        </div>

      </div>
    </div>
  );
}

export default VolDashboard;
